import * as jsvat from '@accountable/jsvat';
import { getInvoiceItemsTotalInclVAT } from 'components/pages/Revenues/OtherRevenueForm/utils';
import type { ObjectSchema, Schema, StringSchema } from 'joi';
import Joi from 'joi';
import { get } from 'lodash';
import type { DictionaryType } from 'types/global.types';
import type { InvoiceItem } from 'types/invoice.types';
import { isValidBIC, isValidIBAN } from 'ibantools';

const checkVAT = (
  VATNumber: string,
  countries?: jsvat.CountryConfig[],
): jsvat.VatCheckResult =>
  jsvat.checkVAT(VATNumber, countries || jsvat.countries);

const validateBankInfo = (
  validate: (value: string) => boolean,
  messages: any,
): StringSchema =>
  Joi.string()
    .allow('', null)
    .replace(/ /g, '')
    .custom((value, helpers) => {
      if (!validate(value)) {
        return helpers.error('any.invalid');
      }
      return value;
    })
    .messages(messages);

// Since the 'fieldName' has a '.' (e.g. 'address.street', 'address.city' ..etc), and Joi has a certain way to
// handle the validation for such grouped form fields, this function adjusts the validation structure to meet
// the Joi's required data structure.
// INPUT: { 'address.street': Joi.string(), 'address.city': Joi.string, }
// OUTPUT: Joi.object({ street: Joi.string(), city: Joi.string() })
const groupValidation = (
  group: string,
  validationObject = {},
): { [x: string]: ObjectSchema<any> } => {
  const _validationObject = Object.entries(validationObject).reduce(
    (acc: any, [fieldName = '', value]) => {
      acc[fieldName.split('.')[1]] = value;

      return acc;
    },
    {},
  );

  return {
    [group]: Joi.object(_validationObject),
  };
};

const getCountryBasedValidation = (
  country: string,
  validationMap: { [x: string]: Schema<any> } = {},
): Schema<any> => validationMap[country as keyof typeof validationMap];

export const stringKeys = [
  'string.base',
  'string.empty',
  'string.email',
  'string.min',
  'string.max',
  'string.pattern.base',
  'string.length',
];

export const numberKeys = [
  'missing.number',
  'number.type',
  'number.base',
  'number.min',
  'number.max',
  'number.empty',
  'number.integer',
];

const keys = [
  'any.only',
  'any.empty',
  'any.required',
  'any.invalid',
  ...stringKeys,
  ...numberKeys,
  'array.min',
  'array.max',
  'array.length',
  'object.required',
  'object.base',
  'date.min',
  'date.max',
  'date.less',
  'date.format',
  'date.base',
  'any.ref',
  'string.pattern.base',
  'date.invalid',
];

const formatErrorMessage = (prefix: string, label: string): DictionaryType => {
  return keys.reduce((acc, val) => {
    // we're returning the same key when .base and .empty
    // to reduce the number of translations needed since
    // for the enduser it's kinda the same problem
    // value is missing
    const parsedKeyValue = val.replace('.base', '.empty');

    return { ...acc, [val]: `${prefix}.${label}.${parsedKeyValue}` };
  }, {});
};

const formatGenericError = (rule?: string, prefix?: string) => {
  const keys = [
    'any.only',
    'any.empty',
    'any.required',
    'any.invalid',
    'string.base',
    'string.empty',
    'string.email',
    'string.min',
    'string.max',
    'string.length',
    'string.alphaNumeric',
    'missing.number',
    'number.type',
    'number.base',
    'number.min',
    'number.max',
    'number.empty',
    'array.min',
    'array.max',
    'object.required',
    'date.min',
    'date.max',
    'date.less',
  ];

  return keys.reduce((acc, key) => {
    const pref = rule === key ? `${prefix}.` : '';
    const parsedKeyValue = key.replace('.base', '.empty');

    return { ...acc, [key]: `${pref}validation.${parsedKeyValue}` };
  }, {});
};

const validatePeriod = Joi.object({
  quarter: Joi.number()
    .valid(1, 2, 3, 4)
    .when(Joi.ref('$user.country'), { is: 'be', then: Joi.required() })
    .messages(formatErrorMessage('invoice', 'periodQuarter')),
  year: Joi.number()
    .max(new Date().getFullYear() + 1)
    .min(new Date().getFullYear() - 10)
    .required()
    .messages(formatErrorMessage('invoice', 'periodYear')),
  month: Joi.number()
    .valid(1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12)
    .messages(formatErrorMessage('invoice', 'periodMonth')),
});

export const validateGenericAmount = (messageKey: string) =>
  numberKeys.reduce((acc, key) => ({ ...acc, [key]: messageKey }), {});

const validatePaymentAmount: Joi.CustomValidator<any> = (value, helpers) => {
  const items = get(
    helpers.state,
    ['ancestors', 0, 'items'],
    [],
  ) as Partial<InvoiceItem>[];

  const total = getInvoiceItemsTotalInclVAT(items);

  if (total > 0) {
    if (value < 10) {
      return helpers.error('number.min');
    }
    return value;
  }

  if (total <= 0) {
    if (value < 0) {
      return helpers.error('number.min_0');
    }
    return value;
  }
};

/**
 * Shared validation for account holder name field
 * Max 100 characters, allows empty/null values
 */
export const validateAccountHolderName = (messages: any) =>
  Joi.string()
    .trim()
    .max(100)
    .allow('', null)
    .messages({
      ...formatGenericError(),
      'string.max': 'settings.account_holder_name.string.max',
      ...messages,
    });

/**
 * Shared validation for IBAN field with conditional requirement
 * IBAN is required if SWIFT or account holder name is present
 * IBAN must be valid if provided
 */
export const validateIBAN = (
  messages: any,
  swiftFieldName = 'SWIFT',
  accountHolderNameFieldName = 'accountHolderName',
) =>
  Joi.any()
    .custom((value, helpers) => {
      const values = helpers.state.ancestors[0];

      const swiftValue = values[swiftFieldName];
      const accountHolderNameValue = values[accountHolderNameFieldName];

      // Check if SWIFT or Account Holder Name is present (handle null/undefined/empty)
      const hasSwift =
        swiftValue &&
        typeof swiftValue === 'string' &&
        swiftValue.trim() !== '';
      const hasAccountHolderName =
        accountHolderNameValue &&
        typeof accountHolderNameValue === 'string' &&
        accountHolderNameValue.trim() !== '';

      // Normalize value: handle null/undefined as empty string, remove spaces
      const normalizedValue =
        value === null || value === undefined
          ? ''
          : String(value).replace(/ /g, '');

      // Check if IBAN is empty
      const isIBANEmpty = normalizedValue.trim() === '';

      // If either SWIFT or Account Holder Name is present but IBAN is empty, require IBAN
      if ((hasSwift || hasAccountHolderName) && isIBANEmpty) {
        return helpers.error('any.required');
      }

      // If IBAN is empty and SWIFT/AccountHolderName are also empty, allow it
      if (isIBANEmpty && !hasSwift && !hasAccountHolderName) {
        return null; // Return null to match expected format
      }

      // If IBAN has a value, validate it
      if (!isIBANEmpty && !isValidIBAN(normalizedValue)) {
        return helpers.error('any.invalid');
      }

      return normalizedValue || null;
    })
    .messages(messages);

/**
 * Shared validation for SWIFT/BIC field
 * Allows empty/null values, validates BIC format if provided
 */
export const validateSWIFT = (messages: any) =>
  validateBankInfo(isValidBIC, messages);

export {
  checkVAT,
  groupValidation,
  getCountryBasedValidation,
  validateBankInfo,
  formatErrorMessage,
  formatGenericError,
  validatePeriod,
  validatePaymentAmount,
};
